import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import LinearProgress from '@mui/material/LinearProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { logError } from '../../../utils/logger';
import { DocumentPrefixRow, documentPrefixesApi } from '../admin.api';

/** What each document's number looks like, with its prefix in place. */
const DOCS: Record<DocumentPrefixRow['docType'], { label: string; sample: (co: string, p: string, n: number) => string }> = {
  QUOTATION: { label: 'Quotation', sample: (co, p) => `${co}/MOTE/${p}/29-016_P1/2026` },
  PROFORMA: { label: 'Proforma Invoice', sample: (co, p, n) => `${co}/${p}/MOTE/${n}/2026-27` },
  INVOICE: { label: 'Tax Invoice', sample: (co, p, n) => `${co}/${p}/MOTE/${n}/2026-27` },
  ADVANCE_RECEIPT: { label: 'Advance Receipt', sample: (co, p) => `${co}/PI/MOTE/3/2026-27/${p}1` },
};

/**
 * Document Master (V36) for one company: the prefix each of its documents is numbered with,
 * and — for the proforma and tax invoice series — the number a financial year's series starts
 * from. Applies to documents raised from now on; numbers already issued keep their prefix.
 */
export default function DocumentPrefixesDialog({
  company,
  canEdit,
  onClose,
}: {
  company: { id: number; name: string; billPrefix?: string | null } | null;
  canEdit: boolean;
  onClose: () => void;
}) {
  const [rows, setRows] = useState<DocumentPrefixRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!company) return;
    let live = true;
    setLoading(true);
    setError(null);
    documentPrefixesApi
      .list(company.id)
      .then((r) => live && setRows(r))
      .catch((err) => live && setError(logError({ message: 'Failed to load the document prefixes', error: err, code: 'DOC_PREFIX_LOAD_FAILED' })))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [company]);

  const set = (i: number, patch: Partial<DocumentPrefixRow>) =>
    setRows((rs) => rs.map((r, n) => (n === i ? { ...r, ...patch } : r)));

  const problem = rows.some((r) => !String(r.prefix ?? '').trim())
    ? 'Every document needs a prefix.'
    : rows.some((r) => String(r.prefix).includes('/'))
    ? 'A prefix cannot contain "/" — it separates the parts of the number.'
    : rows.some((r) => !(Number(r.startNumber) >= 1))
    ? 'A start number must be 1 or more.'
    : null;

  async function save() {
    if (!company || problem) return;
    setSaving(true);
    setError(null);
    try {
      setRows(await documentPrefixesApi.save(company.id, rows));
      toast.success(`Document prefixes saved for ${company.name}`);
      onClose();
    } catch (err) {
      setError(logError({ message: 'Failed to save the document prefixes', error: err, code: 'DOC_PREFIX_SAVE_FAILED' }));
    } finally {
      setSaving(false);
    }
  }

  const co = (company?.billPrefix || 'SCPL').toUpperCase();

  return (
    <Dialog open={Boolean(company)} onClose={saving ? undefined : onClose} maxWidth='md' fullWidth>
      <DialogTitle>Document Master · {company?.name}</DialogTitle>
      <DialogContent>
        {loading && <LinearProgress />}
        {error && <Alert severity='error' sx={{ mb: 2 }}>{error}</Alert>}
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          Each number starts with the company&apos;s bill prefix ({co}); the prefix below names the document. A change
          applies to documents raised from now on.
        </Typography>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Document</TableCell>
              <TableCell sx={{ width: 220 }}>Prefix</TableCell>
              <TableCell sx={{ width: 130 }}>Start number</TableCell>
              <TableCell>Number reads as</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r, i) => {
              const numbered = r.docType === 'PROFORMA' || r.docType === 'INVOICE';
              return (
                <TableRow key={r.docType}>
                  <TableCell>{DOCS[r.docType]?.label ?? r.docType}</TableCell>
                  <TableCell>
                    <TextField size='small' value={r.prefix ?? ''} disabled={!canEdit}
                      onChange={(e) => set(i, { prefix: e.target.value })} inputProps={{ maxLength: 50 }} />
                  </TableCell>
                  <TableCell>
                    {numbered ? (
                      <TextField size='small' type='number' value={r.startNumber ?? 1} disabled={!canEdit}
                        onChange={(e) => set(i, { startNumber: Number(e.target.value) })} inputProps={{ min: 1 }} />
                    ) : (
                      <Typography variant='caption' color='text.secondary'>—</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 13 }}>
                    {DOCS[r.docType]?.sample(co, String(r.prefix ?? '').trim() || '…', Number(r.startNumber) || 1)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 1.5 }}>
          A start number applies to each financial year&apos;s series: its first proforma or tax invoice takes that number,
          or the next free one above the numbers already used.
        </Typography>
        {canEdit && problem && <Alert severity='warning' sx={{ mt: 2 }}>{problem}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>{canEdit ? 'Cancel' : 'Close'}</Button>
        {canEdit && (
          <Button variant='contained' onClick={save} disabled={saving || loading || Boolean(problem)}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
